/**
 * オフライン問題パックの保存・取得・削除・ローカルクエリ。
 * 設計: .steering/20260719-capacitor-store-app/design.md §4.3, §4.4
 */
import { apiGet } from "../api/client";
import { Question } from "../types";
import { getOfflineDB, OfflinePack } from "./db";

const PAGE_LIMIT = 100;

interface QuestionsPageResponse {
  questions: Question[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * 指定区分の全問題を GET /questions のページングで取得し、1パックとして保存する。
 * 途中で失敗した場合は何も保存しない（中断・部分保存しない）。
 */
export async function downloadPack(
  examCategory: string,
  idToken: string | undefined,
  onProgress?: (fetched: number, total: number) => void,
): Promise<OfflinePack> {
  const allQuestions: Question[] = [];
  let offset = 0;
  let total = Infinity;

  while (offset < total) {
    const data = await apiGet<QuestionsPageResponse>(
      "/questions",
      { exam_category: examCategory, limit: PAGE_LIMIT, offset },
      idToken,
    );
    total = data.total;
    if (data.questions.length === 0) break; // 安全弁: 想定外の空応答での無限ループ防止
    allQuestions.push(...data.questions);
    offset += PAGE_LIMIT;
    onProgress?.(allQuestions.length, total);
  }

  const pack: OfflinePack = {
    examCategory,
    questions: allQuestions,
    count: allQuestions.length,
    bytes: JSON.stringify(allQuestions).length,
    savedAt: new Date().toISOString(),
  };

  const db = await getOfflineDB();
  await db.put("packs", pack);
  return pack;
}

export async function getPack(
  examCategory: string,
): Promise<OfflinePack | undefined> {
  const db = await getOfflineDB();
  return db.get("packs", examCategory);
}

export async function getAllPacks(): Promise<OfflinePack[]> {
  const db = await getOfflineDB();
  return db.getAll("packs");
}

export async function deletePack(examCategory: string): Promise<void> {
  const db = await getOfflineDB();
  await db.delete("packs", examCategory);
}

export async function deleteAllPacks(): Promise<void> {
  const db = await getOfflineDB();
  const tx = db.transaction("packs", "readwrite");
  await tx.store.clear();
  await tx.done;
}

export interface PackQueryOptions {
  examSection?: string;
  year?: number;
  season?: string;
  category?: string;
  shuffle?: boolean;
  limit?: number;
  offset?: number;
}

export interface PackQueryResult {
  questions: Question[];
  total: number;
}

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * 保存済みパックに対して、GET /questions と同等のフィルタ・シャッフル・ページングを
 * ローカルで適用する（design §4.4「オフライン時のフォールバック」用）。
 */
export function queryPack(
  pack: OfflinePack,
  opts: PackQueryOptions = {},
): PackQueryResult {
  const filtered = pack.questions.filter((q) => {
    if (opts.examSection && q.examSection !== opts.examSection) return false;
    if (opts.year !== undefined && q.year !== opts.year) return false;
    if (opts.season && q.season !== opts.season) return false;
    if (opts.category && q.category !== opts.category) return false;
    return true;
  });

  const ordered = opts.shuffle ? shuffleArray(filtered) : filtered;
  const total = ordered.length;
  const offset = opts.offset ?? 0;
  const limit = opts.limit ?? 20;

  return { questions: ordered.slice(offset, offset + limit), total };
}
